import {
  Operation,
  OperationResult,
  PreparedMark,
  PreparedOperation,
  ResponseResult,
} from '../reducer/operations/types';

const OPERATION_DIRECTION = {
  income: 'in',
  outcome: 'out',
};

const OPERATION_STATE = {
  posted: 'posted',
  waiting: 'waiting',
  reject: 'reject',
  reversed: 'reversed',
  adjust: 'adjust',
  unknown: 'unknown',
};

const MARK_STATE = {
  waiting: 'waiting',
  added: 'added',
};

const MARK_TYPE = {
  ottCommon: 'ott_common',
  akbarsCommon: 'akbars_common',
  akBarsAdditional: 'akbars_additional',
  akBarsAdditionalSimplyIdentified: 'akbars_additional_simply_identified',
  akBarsAdditionalAnonymous: 'akbars_additional_anonymous',
  ottAdditional: 'ott_additional',
  orderBonuses: 'order_bonuses',
  akbars_common_23_8: 'akbars_common_23_8',
};

const commonBonusMarkType = [
  MARK_TYPE.ottCommon,
  MARK_TYPE.akbarsCommon,
  MARK_TYPE.akbars_common_23_8,
];

const partnerAction = new RegExp(/^(partner_action_)/gi);
const akbarsAction = new RegExp(/^(akbars_action_)/gi);

const isExtraBonusOperation = (marks: string[]): boolean => {
  // если есть только метки "ott_common" и/или "akbars_common" и/или akbars_common_23_8, то кэшбэк считаем обычным и раскрашиваем серым
  let isExtra = false;

  for (let i = 0; i <= marks.length - 1; i++) {
    if (!commonBonusMarkType.includes(marks[i])) {
      isExtra = true;
      break;
    }
  }

  return isExtra;
};

function getIsIncome(direction: string) {
  switch (direction) {
    case OPERATION_DIRECTION.outcome:
      return false;
    case OPERATION_DIRECTION.income:
      return true;
    default:
      return null;
  }
}

function prepareOperation(responseOperation: Operation): PreparedOperation {
  const { marks = [] } = responseOperation;
  const isIncome = getIsIncome(responseOperation.direction);

  const preparedMarks: PreparedMark[] = marks
    .filter(({ amount }) => amount !== 0)
    .map((mark) => ({
      ...mark,
      amount: Math.floor(mark.amount),
      isPending: mark.status === MARK_STATE.waiting,
    }));

  const markTypeList = preparedMarks.map((mark) => mark.type);
  const tripcoinsTotal = preparedMarks.reduce((acc, { amount }) => acc + amount, 0);
  const isFullyAccrued = preparedMarks.every(({ status }) => status === MARK_STATE.added);
  const isPartlyAccrued = preparedMarks.some(({ status }) => status === MARK_STATE.added);
  const isExtraBonus = isExtraBonusOperation(markTypeList);

  const isAction = preparedMarks.some(
    ({ type }) => akbarsAction.test(type) || partnerAction.test(type)
  );

  const result: PreparedOperation = {
    id: responseOperation.eventId,
    date: responseOperation.eventDate,
    isIncome,
    isPending: responseOperation.state === OPERATION_STATE.waiting,
    isCancelled:
      responseOperation.state === OPERATION_STATE.reject ||
      responseOperation.state === OPERATION_STATE.reversed,
    amount: responseOperation.amount,
    currency: responseOperation.currency,
    merchantName: responseOperation.merchantName,
    description: responseOperation.description,
    tripcoinsTotal,
    isFullyAccrued,
    isPartlyAccrued,
    isAction,
    isExtraBonus,
    marks: preparedMarks,
  };

  return result;
}

export default function prepareOperations(responseResult: ResponseResult): OperationResult {
  const { total, set: operations, page: pageNumber, size: pageSize } = responseResult;

  const byId: Record<string, PreparedOperation> = operations.reduce<
    Record<string, PreparedOperation>
  >((acc, responseOperation) => {
    const operation = prepareOperation(responseOperation);
    acc[operation.id.toString()] = operation;

    return acc;
  }, {});

  return {
    byId,
    pageSize,
    pageNumber,
    total,
  };
}
